"use client";
import { useState, useRef } from "react";
import HistogramChart from "./histogram-chart";
import { GaugeChart } from "./gauge-chart";
import { ComparisonChart } from "./comparison-chart";
import { TimeSeriesChart } from "./components/time-series-chart";
import { ExportButtons } from "./components/export-buttons";
import { useSimulation } from "@/components/simulation-context";
import { useI18n } from "@/lib/i18n/hook";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SavedSimulationsList from "./saved-simulations-list";
import { YearlyStat } from "@/lib/simulation";

// Types
interface SimulationParams {
  initialInvestment: number;
  investmentAmount: number;
  investmentPeriod: number;
  returnRate: number;
  volatility: number;
  targetAmount: number;
}

interface SimulationResult {
  params: SimulationParams;
  results: number[];
  yearlyStats?: YearlyStat[]; // Added optional for backward compatibility or strictness
  kernelDensity: { x: number[]; y: number[] };
  successRate: number;
  statistics: {
    mean: number;
    median: number;
    min: number;
    max: number;
    mode: number;
    totalInvestment: number;
  };
}

// Components
function LoadingState() {
  const { t } = useI18n();
  return (
    <div className="flex-1 p-6">
      <h2 className="text-2xl font-bold mb-6">{t("riskSimulation.running")}</h2>
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    </div>
  );
}

function NoResultsState() {
  const { t } = useI18n();
  return (
    <div className="flex-1 p-6">
      <h2 className="text-2xl font-bold mb-6">
        {t("riskSimulation.simulationResults")}
      </h2>
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("riskSimulation.noSimulationRun")}</AlertTitle>
        <AlertDescription>
          {t("riskSimulation.simulationInstruction")}
        </AlertDescription>
      </Alert>
    </div>
  );
}

function SaveSimulationDialog({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
}) {
  const { t } = useI18n();
  const [simulationName, setSimulationName] = useState("");

  const handleSave = () => {
    if (simulationName.trim()) {
      onSave(simulationName);
      setSimulationName("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Save className="h-4 w-4 mr-2" />
          {t("common.save")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("riskSimulation.saveSimulationResult")}</DialogTitle>
          <DialogDescription>
            {t("riskSimulation.saveSimulationDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="simulation-name">
            {t("riskSimulation.simulationName")}
          </Label>
          <Input
            id="simulation-name"
            value={simulationName}
            onChange={(e) => setSimulationName(e.target.value)}
            placeholder={t("riskSimulation.simulationNamePlaceholder")}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!simulationName.trim()}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SimulationConditions({ params }: { params: SimulationParams }) {
  const { t } = useI18n();
  return (
    <div className="mb-4 p-4 bg-muted rounded-lg">
      <h3 className="text-sm font-medium mb-2">
        {t("riskSimulation.simulationConditions")}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="space-y-4">
          <div>
            <p className="text-muted-foreground">
              {t("riskSimulation.initialInvestment")}
            </p>
            <p className="font-medium">
              {params.initialInvestment.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">
              {t("riskSimulation.monthlyInvestment")}
            </p>
            <p className="font-medium">
              {params.investmentAmount.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-muted-foreground">
              {t("riskSimulation.investmentPeriod")}
            </p>
            <p className="font-medium">
              {params.investmentPeriod}
              {t("common.years")}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">
              {t("riskSimulation.annualReturnRate")}
            </p>
            <p className="font-medium">{params.returnRate}%</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-muted-foreground">
              {t("riskSimulation.volatility")}
            </p>
            <p className="font-medium">{params.volatility}%</p>
          </div>
          {params.targetAmount > 0 && (
            <div>
              <p className="text-muted-foreground">
                {t("riskSimulation.targetAmount")}
              </p>
              <p className="font-medium">
                {params.targetAmount.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChartsGrid({ result }: { result: SimulationResult }) {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      {result.yearlyStats && (
        <TimeSeriesChart
          data={result.yearlyStats}
          totalInvestmentFn={(year) =>
            result.params.initialInvestment +
            result.params.investmentAmount * 12 * year
          }
          targetAmount={result.params.targetAmount}
        />
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg shadow-sm p-4 border">
          <h3 className="text-lg font-medium mb-4">
            {t("common.statistics.assetDistribution")}
          </h3>
          <div className="h-80">
            <HistogramChart
              data={result.results}
              kernelDensity={result.kernelDensity}
              targetAmount={result.params.targetAmount}
            />
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm p-4 border">
          <h3 className="text-lg font-medium mb-4">
            {t("common.statistics.targetAchieveRate")}
          </h3>
          <div className="h-80 flex flex-col items-center justify-center">
            <GaugeChart value={Math.round(result.successRate)} />
            <div className="mt-4 text-center">
              <div className="text-3xl font-bold">
                {Math.round(result.successRate)}%
              </div>
              <div className="text-sm text-muted-foreground">
                {t("common.statistics.targetAchieveRate", {
                  amount: result.params.targetAmount.toLocaleString(),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-sm p-4 border">
        <h3 className="text-lg font-medium mb-4">
          {t("common.statistics.title")}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.totalInvestment")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.totalInvestment).toLocaleString()}
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.mean")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.mean).toLocaleString()}
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.median")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.median).toLocaleString()}
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.min")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.min).toLocaleString()}
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.max")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.max).toLocaleString()}
            </div>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">
              {t("common.statistics.mode")}
            </div>
            <div className="text-2xl font-bold">
              {Math.round(result.statistics.mode).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
export function SimulationResult() {
  const {
    simulationResult,
    isLoading,
    saveCurrentSimulation,
    selectedSimulationIds,
  } = useSimulation();
  const { t } = useI18n();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("current");
  const printRef = useRef<HTMLDivElement>(null);

  if (!simulationResult && !isLoading && activeTab === "current") {
    return <NoResultsState />;
  }

  if (isLoading && activeTab === "current") {
    return <LoadingState />;
  }

  return (
    <div className="flex-1 p-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col gap-4 mb-6">
          <h2 className="text-2xl font-bold">
            {t("riskSimulation.simulationResults")}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <TabsList>
              <TabsTrigger value="current">
                {t("riskSimulation.currentResults")}
              </TabsTrigger>
              <TabsTrigger value="saved">
                {t("riskSimulation.savedResults")}
              </TabsTrigger>
              <TabsTrigger
                value="compare"
                disabled={selectedSimulationIds.length < 2}
              >
                {t("riskSimulation.compare")}
              </TabsTrigger>
            </TabsList>
            {simulationResult && activeTab === "current" && (
              <>
                <SaveSimulationDialog
                  open={saveDialogOpen}
                  onOpenChange={setSaveDialogOpen}
                  onSave={saveCurrentSimulation}
                />
                <ExportButtons 
                  result={simulationResult} 
                  printRef={printRef} 
                />
              </>
            )}
          </div>
        </div>

        <TabsContent value="current" className="mt-0">
          {simulationResult && (
            <div ref={printRef} className="p-4 bg-background">
              <SimulationConditions params={simulationResult.params} />
              <ChartsGrid result={simulationResult} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="mt-0">
          <SavedSimulationsList />
        </TabsContent>

        <TabsContent value="compare" className="mt-0">
          <ComparisonChart />
        </TabsContent>
      </Tabs>
    </div>
  );
}
